import type { IRowNode } from "ag-grid-enterprise";

/**
 * Row-model-AGNOSTIC persistence machinery for an editable AG Grid, extracted from
 * VoterList::EditorGridComponent's client-side (CSRM) controller (Hiram SOFT-2, C3 Phase B /
 * Onda 0) so an upcoming server-side (SSRM) editable grid can reuse the SAME queue/serialization/
 * transport instead of growing a second, divergent implementation.
 *
 * Owns:
 *   - The change queue: every `queueChange` call within one synchronous tick coalesces into ONE
 *     microtask flush. Exactly one changed cell on an EXISTING row -> single-cell PATCH. Everything
 *     else (multiple cells/rows, or ANY brand-new row) -> `batch_update`.
 *   - HARD-1 (Hiram) — per-node persistence is SERIALIZED: while a node has a request in flight
 *     (CREATE or UPDATE), a further edit on that same node is held rather than fired as a second,
 *     overlapping request, and replayed as an UPDATE once the in-flight request settles. Closes the
 *     race where typing into a brand-new row faster than the round-trip produced two CREATEs.
 *   - HARD-2 (Hiram) — `batch_update` responses are POSITION-ALIGNED 1:1 with the request's
 *     `changes` (`response[i] <-> entries[i]`, `null` marking a row swept up as fully-blank).
 *
 * Deliberately NOT a Stimulus controller: a plain, injectable class so both the CSRM editor grid
 * and the future SSRM one construct the same queue against their own urls/callbacks. Row-model-
 * specific concerns (blank-row top-up, paste, weight-format gate, column defs) stay in each
 * controller — see VoterList::EditorGridComponent's `editor_grid_component_controller.ts`.
 */

export interface PendingChange {
  node: IRowNode;
  isNew: boolean;
  attributes: Record<string, unknown>;
  oldValues: Record<string, unknown>;
}

export interface GridEditQueueCallbacks {
  // Fired once after ANY successful single-cell or batch mutation — e.g. the stale-highlight cue.
  // `payload` is the raw response body, for consumers that need more than the stale-list cue —
  // the voter-list editor reads the group delta out of it to refresh the Group settings selector.
  onPersisted: (message: string, payload?: Record<string, unknown>) => void;
  // The server (or a failed brand-new row) swept this node away. The caller owns the actual
  // `applyTransaction({remove:...})` plus any row-model-specific bookkeeping (blank-row top-up,
  // per-node validity state) — this class only knows persistence, not grid presentation.
  onRemoveNode: (node: IRowNode) => void;
  // Optional (additive — every existing caller omits this and is unaffected). Fired once per
  // REJECTED single-cell/batch mutation (non-2xx response or a network error), with the extracted
  // server message, right alongside the existing `console.warn`/`console.error` below. Added for
  // VoterList::PostSaveGridComponent (Spec 013 / Onda 3): the server now gates edits on
  // election state (`Failure(type: :not_editable)`, HTTP 422) on a grid where the organizer has no
  // other visible error surface, so a console-only warning would fail silently from their
  // perspective.
  onError?: (message: string) => void;
  // Optional. Values the grid holds that were deliberately never queued (CellLengthGuard's
  // too-long cells); overlaid on a server row so saving a neighbouring cell does not wipe them.
  unsentValues?: (node: IRowNode) => Record<string, unknown>;
}

// The STALE-HIGHLIGHT-CUE wording shown after a successful mutation.
export const STALE_HIGHLIGHT_MESSAGE = "The voter list has changed. Please validate again.";

export function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
}

export interface JsonResponse {
  ok: boolean;
  status: number;
  json: Record<string, unknown>;
}

// Rails answers an unrescued error with its static HTML page (404/500), not JSON. `response.json()`
// would reject on it and route a perfectly ordinary server rejection into the callers' network-error
// path, where no status is available and nothing is shown to the organizer.
function parseJsonResponse(response: Response): Promise<JsonResponse> {
  return response
    .json()
    .catch(() => ({}))
    .then((json) => ({ ok: response.ok, status: response.status, json }));
}

export function postJson(url: string): Promise<JsonResponse> {
  return fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "same-origin",
  }).then(parseJsonResponse);
}

export function patchJson(url: string, body: unknown): Promise<JsonResponse> {
  return fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "same-origin",
    body: JSON.stringify(body),
  }).then(parseJsonResponse);
}

export const ROW_GONE_MESSAGE =
  "This voter is no longer in the list (it was removed or left blank), so the edit was not saved. " +
  "Add the voter again if needed.";

export function extractErrorMessage(json: Record<string, unknown>, status?: number): string {
  if (typeof json.errors === "string") return json.errors;
  if (json.errors && typeof json.errors === "object") {
    return Object.values(json.errors as Record<string, string[]>).flat().join(", ");
  }
  return status ? `Update failed (server responded ${status})` : "Update failed";
}

export const NETWORK_ERROR_MESSAGE =
  "The change could not be saved (connection problem). Check your connection and try again.";

function rollbackKey(node: IRowNode, field: string): string {
  return `${String(node.id)}\u0000${field}`;
}

// AG Grid hands back the value it read from `data`, so a revert to `null` can echo as `undefined`
// (or "" for text editors) — treat every blank the same.
function valuesMatch(a: unknown, b: unknown): boolean {
  const blank = (v: unknown) => v === null || v === undefined || v === "";
  if (blank(a) && blank(b)) return true;
  return Object.is(a, b);
}

export class GridEditQueue {
  // Keyed by AG Grid's own (stable, client-assigned) `node.id` — NOT `data.id`, since a brand-new
  // row has no `data.id` until it is first persisted.
  private pendingChanges: Map<string, PendingChange> = new Map();
  private flushScheduled = false;
  // Rollback echo guard. `rollback`'s `node.setDataValue` makes AG Grid fire `cellValueChanged`,
  // and the gridOptions `onCellValueChanged` callback is dispatched ASYNCHRONOUSLY (setTimeout 0 —
  // GridOptionsService registers it as an async global listener; only gridPreDestroyed/fillStart/
  // pasteStart are sync). A synchronous "suppress while reverting" flag therefore never covers the
  // echo: the handler ran after the flag was cleared, re-queued the revert as an edit, and on any
  // PERSISTENT failure (row deleted server-side -> 404, offline) the two values ping-ponged forever
  // at ~5 PATCHes/s per cell (Rollbar item 1806211828). Instead, each revert is recorded per
  // node+field with the value it wrote, and `isRollbackEcho` consumes the matching event.
  private pendingRollbacks: Map<string, unknown> = new Map();

  // HARD-1 — per-node persistence serialization. `inFlightNodes` holds the `node.id` keys of every
  // node with a CREATE or UPDATE currently in flight; `heldChanges` accumulates any edit that lands
  // on one of those nodes before its in-flight request settles.
  private inFlightNodes: Set<string> = new Set();
  private heldChanges: Map<string, PendingChange> = new Map();
  private requests: Set<Promise<void>> = new Set();

  constructor(
    private readonly updateUrlTemplate: string,
    private readonly batchUpdateUrl: string,
    private readonly callbacks: GridEditQueueCallbacks,
    private readonly logLabel: string = "[GridEditQueue]"
  ) {}

  // Callers' onCellValueChanged handlers call this first and skip the event when it returns true.
  // Any event for a reverted cell consumes its record; only one carrying the reverted value is an
  // echo — a different value means the organizer edited the cell again and that edit must persist.
  isRollbackEcho(node: IRowNode, field: string, newValue: unknown): boolean {
    const key = rollbackKey(node, field);
    if (!this.pendingRollbacks.has(key)) return false;

    const revertedTo = this.pendingRollbacks.get(key);
    this.pendingRollbacks.delete(key);
    return valuesMatch(revertedTo, newValue);
  }

  queueChange(node: IRowNode, field: string, newValue: unknown, oldValue: unknown): void {
    const key = String(node.id);
    const data = node.data as Record<string, unknown>;
    const existing = this.pendingChanges.get(key) ?? {
      node,
      isNew: data?.id === undefined || data?.id === null,
      attributes: {},
      oldValues: {},
    };

    if (!(field in existing.oldValues)) {
      existing.oldValues[field] = oldValue;
    }
    existing.attributes[field] = newValue;
    this.pendingChanges.set(key, existing);

    this.scheduleFlush();
  }

  scheduleFlush(): void {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    queueMicrotask(() => this.flush());
  }

  // Public (additive — was private; every existing caller only ever reached this indirectly via
  // `scheduleFlush`'s microtask and is unaffected). Added for the SSRM-exclusive hazard a purely
  // microtask-scheduled flush cannot close: `sort`/`filter` model changes (and any explicit
  // `refreshServerSide({purge:true})`) discard the SSRM cache block SYNCHRONOUSLY, in the SAME
  // call stack that stopped the active cell edit that triggered them — a change queued moments
  // earlier can therefore still be sitting unsent in `pendingChanges` when the purge runs, because
  // the microtask that would have sent it hasn't had a turn yet. Callers on an SSRM row model
  // (see VoterList::PostSaveGridComponent's controller) must call this SYNCHRONOUSLY,
  // as the first statement of their `onSortChanged`/`onFilterChanged` grid-option handlers (and
  // before any manual `refreshServerSide({purge:true})` they issue themselves), so the request is
  // already dispatched before the purge can discard the row node it targets. Idempotent: if the
  // microtask this pre-empts fires later anyway, it finds `pendingChanges` already empty and no-ops.
  flush(): void {
    this.flushScheduled = false;
    if (this.pendingChanges.size === 0) return;

    const entries = Array.from(this.pendingChanges.values());
    this.pendingChanges.clear();

    const toSend: PendingChange[] = [];

    entries.forEach((entry) => {
      const key = String(entry.node.id);

      // HARD-1 — a CREATE or UPDATE for this node is already in flight: hold this edit instead of
      // firing a second, overlapping request for the same row. Replayed (see `releaseInFlight`)
      // once the in-flight request settles, by which point `data.id` (if that request was this
      // node's CREATE) has been assigned — so the replay is always an UPDATE, never a duplicate
      // CREATE.
      if (this.inFlightNodes.has(key)) {
        this.holdChange(key, entry);
        return;
      }

      toSend.push(entry);
    });

    if (toSend.length === 0) return;

    if (toSend.length === 1 && !toSend[0].isNew) {
      this.sendSingleUpdate(toSend[0]);
    } else {
      this.sendBatchUpdate(toSend);
    }
  }

  isIdle(): boolean {
    return (
      this.pendingChanges.size === 0 && this.heldChanges.size === 0 && this.requests.size === 0
    );
  }

  // Resolves once nothing is queued, held or in flight — a page reload before then drops those writes.
  async drain(): Promise<void> {
    this.flush();
    while (this.requests.size > 0) {
      await Promise.all(Array.from(this.requests));
      this.flush();
    }
  }

  private track(request: Promise<void>): void {
    const settled = request.catch(() => undefined);
    this.requests.add(settled);
    void settled.then(() => this.requests.delete(settled));
  }

  // Merges a held edit into any edit already held for the same node — mirrors `queueChange`'s own
  // "keep the first oldValue, overwrite with the latest newValue" coalescing so a rollback (if the
  // in-flight request the held edit is waiting on ultimately fails) restores the correct pre-edit
  // value.
  private holdChange(key: string, entry: PendingChange): void {
    const existing = this.heldChanges.get(key);
    if (!existing) {
      this.heldChanges.set(key, entry);
      return;
    }

    Object.entries(entry.oldValues).forEach(([field, value]) => {
      if (!(field in existing.oldValues)) existing.oldValues[field] = value;
    });
    Object.assign(existing.attributes, entry.attributes);
  }

  private markInFlight(entries: PendingChange[]): void {
    entries.forEach((entry) => this.inFlightNodes.add(String(entry.node.id)));
  }

  // HARD-1 — called once each entry's in-flight request has settled (success, rejection, or
  // network error/rollback). Releases the per-node lock and, if an edit landed on that node while
  // it was locked, re-queues it as a normal pending change — with `isNew` freshly recomputed off
  // the node's CURRENT `data.id` (not the stale value captured when the edit was queued), so a
  // held edit on a just-created row replays as an UPDATE, and one on a just-FAILED create replays
  // as a fresh CREATE.
  private releaseInFlight(entries: PendingChange[]): void {
    entries.forEach((entry) => {
      const key = String(entry.node.id);
      this.inFlightNodes.delete(key);

      const held = this.heldChanges.get(key);
      if (!held) return;

      this.heldChanges.delete(key);
      // Defensive guard: if this node was REMOVED (rowIndex goes null on
      // `applyTransaction({remove:...})`) while its prior request was in flight, there is no row
      // left to replay the held edit against.
      if (held.node.rowIndex === null) return;
      held.isNew = (held.node.data as Record<string, unknown>)?.id == null;
      this.pendingChanges.set(key, held);
      this.scheduleFlush();
    });
  }

  private sendSingleUpdate(entry: PendingChange): void {
    const rowId = (entry.node.data as Record<string, unknown>)?.id;
    if (rowId === undefined || rowId === null) return; // defensive — isNew already routes elsewhere

    this.markInFlight([entry]);
    const url = this.updateUrlTemplate.replace(":id", String(rowId));

    const request = patchJson(url, entry.attributes)
      .then(({ ok, status, json }) => {
        if (status === 404) {
          // The row no longer exists server-side (blanked out and auto-destroyed, or deleted from
          // another tab/by another organizer): nothing to roll back TO — drop the stale row instead
          // so the next keystroke cannot target it again.
          this.callbacks.onRemoveNode(entry.node);
          console.warn(`${this.logLabel} update target gone (404):`, url);
          this.callbacks.onError?.(ROW_GONE_MESSAGE);
          return;
        }

        if (!ok) {
          this.rollback(entry);
          const message = extractErrorMessage(json, status);
          console.warn(`${this.logLabel} update rejected:`, message);
          this.callbacks.onError?.(message);
          return;
        }

        if (json.row) {
          this.applyServerRow(entry, json.row as Record<string, unknown>);
        } else {
          // Swept up as fully-blank server-side (VoterInformationLine's after_save destroy) —
          // mirrors sendBatchUpdate's null-row reconciliation below.
          this.callbacks.onRemoveNode(entry.node);
        }
        this.callbacks.onPersisted(STALE_HIGHLIGHT_MESSAGE, json);
      })
      .catch((error) => {
        this.rollback(entry);
        console.error(`${this.logLabel} PATCH error:`, error);
        this.callbacks.onError?.(NETWORK_ERROR_MESSAGE);
      })
      .finally(() => {
        this.releaseInFlight([entry]);
      });
    this.track(request);
  }

  private sendBatchUpdate(entries: PendingChange[]): void {
    this.markInFlight(entries);

    const changes = entries.map((e) => ({
      id: e.isNew ? null : (e.node.data as Record<string, unknown>).id,
      attributes: e.attributes,
    }));

    const request = patchJson(this.batchUpdateUrl, { changes })
      .then(({ ok, status, json }) => {
        if (!ok) {
          entries.forEach((entry) => this.discardFailedEntry(entry));
          const message = extractErrorMessage(json, status);
          console.warn(`${this.logLabel} batch_update rejected:`, message);
          this.callbacks.onError?.(message);
          return;
        }

        // HARD-2 — the response is POSITION-ALIGNED 1:1 with `changes` (a `null` entry marks a row
        // the server swept up as fully-blank), so `response[i] <-> entries[i]` zips uniformly for
        // existing and newly-created rows alike. Never re-derive alignment from an
        // existing/new split or a `remainingRows` filter — either breaks the instant a
        // NON-terminal new row in the same batch goes blank, since the server drops it from the
        // response and shifts every subsequent positional zip onto the wrong client node.
        const rows = (Array.isArray(json.rows) ? json.rows : []) as (Record<string, unknown> | null)[];

        entries.forEach((entry, index) => {
          const row = rows[index];
          if (row) {
            this.applyServerRow(entry, row);
          } else {
            this.callbacks.onRemoveNode(entry.node);
          }
        });
        this.callbacks.onPersisted(STALE_HIGHLIGHT_MESSAGE, json);
      })
      .catch((error) => {
        entries.forEach((entry) => this.discardFailedEntry(entry));
        console.error(`${this.logLabel} batch_update error:`, error);
        this.callbacks.onError?.(NETWORK_ERROR_MESSAGE);
      })
      .finally(() => {
        this.releaseInFlight(entries);
      });
    this.track(request);
  }

  // Applies a server row WITHOUT clobbering input the organizer typed after the request that
  // produced it was dispatched.
  //
  // `IRowNode#setData` REPLACES the row's data outright (AG Grid: "Replaces the data on the
  // rowNode... refreshes the entire rendered row"), and the server row can only ever reflect the
  // fields the settling request actually carried. Any edit that landed on this node while that
  // request was in flight was deliberately HELD (HARD-1) rather than sent, so it is absent from the
  // response — and applying the response verbatim blanked those cells until the held edit's own
  // replay round-trip landed and filled them back in. That is the "type fast, the value vanishes
  // and comes back a moment later" flicker (reported 2026-08-10); it only ever showed up when
  // editing faster than one round trip, which is exactly when a held change exists.
  //
  // Both maps are consulted because a change can be in either when the response settles: `held`
  // for edits the flush already deferred behind the in-flight request, `pending` for one queued
  // since (its own flush has not run yet). `pending` is applied last — it is the newer of the two.
  // Neither is consumed here: each still flushes on its own, so the overlay only fixes what is
  // DISPLAYED in the gap, and never drops a write.
  private applyServerRow(entry: PendingChange, row: Record<string, unknown>): void {
    const key = String(entry.node.id);
    const unsent = {
      ...(this.heldChanges.get(key)?.attributes ?? {}),
      ...(this.pendingChanges.get(key)?.attributes ?? {}),
      ...(this.callbacks.unsentValues?.(entry.node) ?? {}),
    };

    entry.node.setData(Object.keys(unsent).length === 0 ? row : { ...row, ...unsent });
  }

  // A node already removed from the grid (`rowIndex === null`) gets no revert: there is no cell to
  // show it in, and `setDataValue` on a detached node would fire an echo nothing ever consumes.
  private rollback(entry: PendingChange): void {
    if (entry.node.rowIndex === null) return;

    Object.entries(entry.oldValues).forEach(([field, value]) => {
      this.pendingRollbacks.set(rollbackKey(entry.node, field), value);
      entry.node.setDataValue(field, value);
    });
  }

  // A FAILED edit on a brand-new (never-persisted) row has nothing worth rolling back TO — its
  // "old" values were already blank, so `rollback` would just recreate the same blank row the
  // caller's auto-append-on-first-edit logic already added right behind it. Removing it outright
  // avoids the duplicate. An existing (previously-persisted) row still gets the normal
  // value-revert `rollback`.
  private discardFailedEntry(entry: PendingChange): void {
    if (entry.isNew) {
      this.callbacks.onRemoveNode(entry.node);
    } else {
      this.rollback(entry);
    }
  }
}
